import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { UnvoidableReason } from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { GroupReplacement as NewShipmentAfterVoidResponse } from "@redotech/redo-merchant-fulfillment-rpc/schema/get-replacement-groups";

export function appendNewShipmentsForVoidedGroups(
  groups: FulfillmentGroupData[],
  newShipmentResponses: NewShipmentAfterVoidResponse[],
): FulfillmentGroupData[] {
  return newShipmentResponses.reduce((currentGroups, newShipmentResponse) => {
    const groupIndex = currentGroups.findIndex(
      (group) => group._id === newShipmentResponse.groupId,
    );
    if (groupIndex === -1) {
      return currentGroups;
    }

    const newShipmentAlreadyExists = currentGroups.some(
      (group) => group._id === newShipmentResponse.replacementGroup._id,
    );
    if (newShipmentAlreadyExists) {
      return currentGroups;
    }

    const nextGroups = [...currentGroups];
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const voidedGroup = nextGroups[groupIndex]!;
    nextGroups[groupIndex] = {
      ...voidedGroup,
      status: FulfillmentGroupStatus.Voided,
      pointerToNextGroup: newShipmentResponse.replacementGroup._id,
      shipmentSummary: voidedGroup.shipmentSummary
        ? {
            ...voidedGroup.shipmentSummary,
            carrierShipments: voidedGroup.shipmentSummary.carrierShipments.map(
              (cs) => ({ ...cs, voided: true }),
            ),
            voidActionSummary: {
              voidable: false,
              reason: UnvoidableReason.ALREADY_VOIDED,
            },
          }
        : voidedGroup.shipmentSummary,
    };
    nextGroups.push(newShipmentResponse.replacementGroup);
    return nextGroups;
  }, groups);
}

export function applyWatchedFulfillmentGroupUpdates({
  groups,
  updates,
  hasPendingUpdate,
  groupsWaitingForNewShipment,
  voidedGroupNewShipmentIds,
}: {
  groups: FulfillmentGroupData[];
  updates: FulfillmentGroupData[];
  hasPendingUpdate: (groupId: string) => boolean;
  groupsWaitingForNewShipment: string[];
  voidedGroupNewShipmentIds: Record<string, string>;
}): FulfillmentGroupData[] {
  const newGroups = [...groups];
  for (const group of updates) {
    if (hasPendingUpdate(group._id)) {
      continue;
    }

    const index = newGroups.findIndex((row) => row._id === group._id);
    const waitingForNewShipment =
      groupsWaitingForNewShipment.includes(group._id) &&
      group.pointerToNextGroup;
    const receivedStaleVoidedGroupUpdate =
      voidedGroupNewShipmentIds[group._id] && !group.pointerToNextGroup;
    if (
      index >= 0 &&
      !waitingForNewShipment &&
      !receivedStaleVoidedGroupUpdate
    ) {
      newGroups[index] = group;
    }
  }
  return newGroups;
}
